// components/KPIKRAReports.jsx
import React, { useState, useMemo } from 'react'
import { useDataQuery } from '@dhis2/app-runtime'
import { Card, LinearLoader } from '@dhis2/ui'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Cell as PieCell, Legend } from 'recharts'
import { BarChart3, SearchIcon, PieChart as PieChartIcon, ViewIcon, CheckCircle } from 'lucide-react'
import { GiProgression } from 'react-icons/gi'

const KPIKRAReports = ({ orgUnitId, performanceData }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [kpiSortBy, setKpiSortBy] = useState('progress');
  const [kpiSortOrder, setKpiSortOrder] = useState('desc');
  const [kpiSearchTerm, setKpiSearchTerm] = useState('');
  const [activeChart, setActiveChart] = useState('both'); // 'kpi', 'kra', or 'both'

  // FIXED: Use correct API endpoints and data structure
  const { loading, error, data } = useDataQuery({
    objectives: {
      resource: 'events',
      params: {
        orgUnit: orgUnitId,
        program: 'yiT00XKrrHP',
        programStage: 'VIBtpvkpHFR',
        ouMode: 'ACCESSIBLE',
        pageSize: 1000,
        fields: 'event,orgUnit,orgUnitName,eventDate,dataValues[dataElement,value]'
      }
    },
    kpis: {
      resource: 'events',
      params: {
        orgUnit: orgUnitId,
        program: 'yiT00XKrrHP',
        programStage: 'ivyCLjNLFrd',
        ouMode: 'ACCESSIBLE',
        pageSize: 1000,
        fields: 'event,orgUnit,orgUnitName,eventDate,dataValues[dataElement,value]'
      }
    },
    kras: {
      resource: 'events',
      params: {
        orgUnit: orgUnitId,
        program: 'yiT00XKrrHP',
        programStage: 'BJb3qTgbcTF',
        ouMode: 'ACCESSIBLE',
        pageSize: 1000,
        fields: 'event,orgUnit,orgUnitName,eventDate,dataValues[dataElement,value]'
      }
    },
    tasks: {
      resource: 'events',
      params: {
        orgUnit: orgUnitId,
        program: 'yiT00XKrrHP',
        programStage: 'Nv9nYNcBTdM',
        ouMode: 'ACCESSIBLE',
        pageSize: 10000,
        fields: 'event,orgUnit,orgUnitName,eventDate,dataValues[dataElement,value]'
      }
    }
  }, {
    variables: { refreshKey }
  })

  const effectiveData = performanceData || data

  // FIXED: Debug function to check actual data
  const debugData = () => {
    console.log('🔍 KPIKRAReports Data:', {
      objectives: effectiveData?.objectives?.events?.length || 0,
      kpis: effectiveData?.kpis?.events?.length || 0,
      kras: effectiveData?.kras?.events?.length || 0,
      tasks: effectiveData?.tasks?.events?.length || 0,
      rawData: effectiveData
    });
    
    // Log first KPI data values to see actual structure
    if (effectiveData?.kpis?.events?.[0]) {
      console.log('🔍 First KPI:', effectiveData.kpis.events[0]);
      console.log('🔍 First KPI DataValues:', effectiveData.kpis.events[0].dataValues);
    }
  };

  React.useEffect(() => {
    if (effectiveData) {
      debugData();
    }
  }, [effectiveData]);

  // Function to extract Objective ID from data values
  const getObjectiveId = (dataValues) => {
    return dataValues?.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value || '';
  };

  // Function to calculate task progress for an objective
  const calculateObjectiveProgress = (objectiveId, allTasks) => {
    if (!allTasks || allTasks.length === 0) return 0;

    // Find tasks linked to this objective
    const objectiveTasks = allTasks.filter(task => {
      const taskObjectiveId = getObjectiveId(task.dataValues);
      return taskObjectiveId === objectiveId;
    });

    if (objectiveTasks.length === 0) return 0;

    // Calculate progress based on task completion status
    const totalTasks = objectiveTasks.length;
    const completedTasks = objectiveTasks.filter(task => {
      const status = task.dataValues?.find(dv => dv.dataElement === 'LuTwfuN34pc')?.value;
      return status === 'completed' || status === 'Completed';
    }).length;

    // Also consider task percentage complete
    const totalProgress = objectiveTasks.reduce((sum, task) => {
      const progressValue = task.dataValues?.find(dv => dv.dataElement === 'AElpNxzTdRh')?.value;
      const progress = parseInt(progressValue) || 0;
      return sum + Math.min(Math.max(progress, 0), 100);
    }, 0);

    const averageProgress = totalProgress / totalTasks;
    
    // Weight completion rate higher than average progress
    const completionRate = (completedTasks / totalTasks) * 100;
    const weightedProgress = (completionRate * 0.7) + (averageProgress * 0.3);
    
    return Math.round(Math.min(weightedProgress, 100));
  };

  // FIXED: Enhanced KPI extraction with proper data access
  const extractKPIs = useMemo(() => {
    if (!effectiveData?.kpis?.events) {
      console.log('🟡 No KPI events found');
      return [];
    }
    
    const allObjectives = effectiveData?.objectives?.events || [];
    const allTasks = effectiveData?.tasks?.events || [];
    
    console.log('🟡 Processing KPIs:', {
      kpiCount: effectiveData.kpis.events.length,
      objectiveCount: allObjectives.length,
      taskCount: allTasks.length
    });
    
    const processed = effectiveData.kpis.events.map(kpi => {
      const dataValues = kpi.dataValues || [];
      
      // FIXED: Use correct data element IDs from your structure
      const name = dataValues.find(dv => dv.dataElement === 'DBmylDQA9KX')?.value || 'Unnamed KPI';
      const description = dataValues.find(dv => dv.dataElement === 'rQqF75V9t7K')?.value || '';
      const targetValue = dataValues.find(dv => dv.dataElement === 'Ru68Ax2Xo2L')?.value;
      const target = targetValue ? parseInt(targetValue) : 100;
      
      // Get the parent objective ID for this KPI
      const parentObjectiveId = getObjectiveId(dataValues);
            
      
      // Calculate progress based on parent objective's task completion
      const progress = parentObjectiveId ? 
        calculateObjectiveProgress(parentObjectiveId, allTasks) : 0;
      
      // Find the parent objective for additional context
      const parentObjective = allObjectives.find(obj => 
        obj.event === parentObjectiveId || getObjectiveId(obj.dataValues) === parentObjectiveId
      );

      const achievement = Math.min(Math.max(progress, 0), 100);
      
      const linkedTasks = allTasks.filter(task => 
        getObjectiveId(task.dataValues) === parentObjectiveId
      );
      
      const completedTasks = linkedTasks.filter(task => {
        const status = task.dataValues?.find(dv => dv.dataElement === 'LuTwfuN34pc')?.value;
        return status === 'completed' || status === 'Completed';
      });

      return {
        id: kpi.event,
        name,
        description,
        target,
        progress: achievement,
        achievement,
        date: kpi.eventDate,
        parentObjectiveId,
        parentObjectiveName: parentObjective ? 
          parentObjective.dataValues?.find(dv => dv.dataElement === 'EuN5ztFnXsQ')?.value || 'Unknown Objective' : 'No Parent Objective',
        linkedTasks: linkedTasks.length,
        completedTasks: completedTasks.length
      };
    });

    console.log('✅ Processed KPIs:', processed);
    return processed;
  }, [effectiveData?.kpis, effectiveData?.objectives, effectiveData?.tasks]);

  // FIXED: Enhanced KRA extraction
  const extractKRAs = useMemo(() => {
    if (!effectiveData?.kras?.events) {
      console.log('🟡 No KRA events found');
      return [];
    }
    
    const kpis = extractKPIs;
    const allObjectives = effectiveData?.objectives?.events || [];
    
    const processed = effectiveData.kras.events.map(kra => {
      const dataValues = kra.dataValues || [];
      
      // FIXED: Use correct data element IDs from your structure
      const name = dataValues.find(dv => dv.dataElement === 'obapz6Gnyla')?.value || 'Unnamed KRA';
      const description = dataValues.find(dv => dv.dataElement === 'DEECWPTj42E')?.value || '';
      const weightValue = dataValues.find(dv => dv.dataElement === 'Xl4VEFoIFIw')?.value;
      const weight = weightValue ? parseInt(weightValue) : 0;
      
      // Get the parent objective ID for this KRA
      const parentObjectiveId = getObjectiveId(dataValues);
      
      // Find KPIs linked to the same objective
      const linkedKPIs = kpis.filter(kpi => kpi.parentObjectiveId === parentObjectiveId);

      // Calculate KRA progress based on weighted KPI progress
      const kraProgress = linkedKPIs.length > 0 
        ? Math.round(linkedKPIs.reduce((sum, kpi) => sum + kpi.progress, 0) / linkedKPIs.length)
        : 0;

      // Find the parent objective for additional context
      const parentObjective = allObjectives.find(obj => 
        obj.event === parentObjectiveId || getObjectiveId(obj.dataValues) === parentObjectiveId
      );

      return {
        id: kra.event,
        name,
        description,
        weight,
        progress: kraProgress,
        date: kra.eventDate,
        parentObjectiveId,
        parentObjectiveName: parentObjective ? 
          parentObjective.dataValues?.find(dv => dv.dataElement === 'EuN5ztFnXsQ')?.value || 'Unknown Objective' : 'No Parent Objective',
        linkedKPIs: linkedKPIs.length,
        kpiDetails: linkedKPIs
      };
    });

    console.log('✅ Processed KRAs:', processed);
    return processed;
  }, [effectiveData?.kras, extractKPIs, effectiveData?.objectives]);

  // Calculate overall statistics
  const statistics = useMemo(() => {
    const kpis = extractKPIs;
    const kras = extractKRAs;
    const objectives = data?.objectives?.events || [];
    const tasks = data?.tasks?.events || [];
    
    const totalKPIs = kpis.length;
    const totalKRAs = kras.length;
    const totalObjectives = objectives.length;
    const totalTasks = tasks.length;
    
    // Calculate average progress
    const avgKPIProgress = totalKPIs > 0 
      ? Math.round(kpis.reduce((sum, kpi) => sum + kpi.progress, 0) / totalKPIs)
      : 0;
      
    const avgKRAProgress = totalKRAs > 0
      ? Math.round(kras.reduce((sum, kra) => sum + kra.progress, 0) / totalKRAs)
      : 0;

    // Calculate completion rates
    const completedKPIs = kpis.filter(kpi => kpi.progress >= 100).length;
    const completedKRAs = kras.filter(kra => kra.progress >= 100).length;
    const completedTasks = tasks.filter(task => {
      const status = task.dataValues?.find(dv => dv.dataElement === 'LuTwfuN34pc')?.value;
      return status === 'completed' || status === 'Completed';
    }).length;

    // Calculate task completion rate
    const taskCompletionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    return {
      totalKPIs,
      totalKRAs,
      totalObjectives,
      totalTasks,
      avgKPIProgress,
      avgKRAProgress,
      completedKPIs,
      completedKRAs,
      completedTasks,
      taskCompletionRate,
      kpiCompletionRate: totalKPIs > 0 ? Math.round((completedKPIs / totalKPIs) * 100) : 0,
      kraCompletionRate: totalKRAs > 0 ? Math.round((completedKRAs / totalKRAs) * 100) : 0
    };
  }, [extractKPIs, extractKRAs, data?.objectives, data?.tasks]);

  // Prepare KPI distribution data for pie chart
  const kpiDistributionData = useMemo(() => {
    const kpis = extractKPIs;
    const excellent = kpis.filter(k => k.progress >= 80).length;
    const good = kpis.filter(k => k.progress >= 60 && k.progress < 80).length;
    const needsWork = kpis.filter(k => k.progress >= 40 && k.progress < 60).length;
    const poor = kpis.filter(k => k.progress < 40).length;
    
    return [
      { name: 'Excellent (≥80%)', value: excellent, color: '#00C49F', progress: '80-100%' },
      { name: 'Good (60-79%)', value: good, color: '#FFBB28', progress: '60-79%' },
      { name: 'Needs Work (40-59%)', value: needsWork, color: '#FF8042', progress: '40-59%' },
      { name: 'Poor (<40%)', value: poor, color: '#FF4444', progress: '0-39%' }
    ].filter(item => item.value > 0);
  }, [extractKPIs]);

  // Prepare KRA distribution data for pie chart
  const kraDistributionData = useMemo(() => {
    const kras = extractKRAs;
    const excellent = kras.filter(k => k.progress >= 80).length;
    const good = kras.filter(k => k.progress >= 60 && k.progress < 80).length;
    const needsWork = kras.filter(k => k.progress >= 40 && k.progress < 60).length;
    const poor = kras.filter(k => k.progress < 40).length;
    
    return [
      { name: 'Excellent (≥80%)', value: excellent, color: '#00C49F', progress: '80-100%' },
      { name: 'Good (60-79%)', value: good, color: '#FFBB28', progress: '60-79%' },
      { name: 'Needs Work (40-59%)', value: needsWork, color: '#FF8042', progress: '40-59%' },
      { name: 'Poor (<40%)', value: poor, color: '#FF4444', progress: '0-39%' }
    ].filter(item => item.value > 0);
  }, [extractKRAs]);

  // Prepare aggregated gauge data
  const aggregatedData = useMemo(() => {
    return {
      kpi: {
        average: statistics.avgKPIProgress,
        completed: statistics.completedKPIs,
        total: statistics.totalKPIs,
        completionRate: statistics.kpiCompletionRate
      },
      kra: {
        average: statistics.avgKRAProgress,
        completed: statistics.completedKRAs,
        total: statistics.totalKRAs,
        completionRate: statistics.kraCompletionRate
      }
    };
  }, [statistics]);

  // Sort and filter KPIs for table view
  const sortedKPIs = useMemo(() => {
    const sorted = [...extractKPIs];
    sorted.sort((a, b) => {
      let aVal, bVal;
      switch(kpiSortBy) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'progress':
          aVal = a.progress;
          bVal = b.progress;
          break;
        case 'tasks':
          aVal = a.completedTasks / (a.linkedTasks || 1);
          bVal = b.completedTasks / (b.linkedTasks || 1);
          break;
        default:
          aVal = a.progress;
          bVal = b.progress;
      }
      if (kpiSortOrder === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });
    return sorted;
  }, [extractKPIs, kpiSortBy, kpiSortOrder]);

  const filteredKPIs = useMemo(() => {
    if (!kpiSearchTerm) return sortedKPIs;
    return sortedKPIs.filter(kpi => 
      kpi.name.toLowerCase().includes(kpiSearchTerm.toLowerCase()) ||
      kpi.parentObjectiveName.toLowerCase().includes(kpiSearchTerm.toLowerCase())
    );
  }, [sortedKPIs, kpiSearchTerm]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Custom tooltip for pie charts
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '8px 12px',
          border: '1px solid #ccc',
          borderRadius: 4,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>{data.name}</p>
          <p style={{ margin: '4px 0 0 0', color: '#666' }}>
            Count: <strong>{data.value}</strong>
          </p>
          <p style={{ margin: '2px 0 0 0', color: '#666', fontSize: 11 }}>
            Progress: {data.progress}
          </p>
        </div>
      );
    }
    return null;
  };

  // Determine which visualization to use
  const useTableView = extractKPIs.length > 8;

  if (loading && !performanceData) return (
    <Card style={{ padding: 16 }}>
      <h3>KPI & KRA Performance Dashboard</h3>
      <LinearLoader />
      <div style={{ textAlign: 'center', marginTop: 8, color: '#666' }}>
        Loading performance data...
      </div>
    </Card>
  )

  if (error && !performanceData) {
    console.error('❌ KPIKRAReports Error:', error);
    return (
      <Card style={{ padding: 16, backgroundColor: '#f8d7da' }}>
        <h3>KPI & KRA Performance Dashboard</h3>
        <div style={{ color: '#721c24' }}>
          Error loading performance data: {error.message}
        </div>
        <button 
          onClick={handleRefresh}
          style={{
            marginTop: 12,
            padding: '8px 16px',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </Card>
    );
  }

  if (data && extractKPIs.length === 0) {
    return (
      <Card style={{ padding: 24, margin: 16 }}>
        <h3>KPIs & KRAs Performance</h3>
        <div style={{ 
          textAlign: 'center', 
          padding: 40, 
          color: '#6c757d',
          backgroundColor: '#f8f9fa',
          borderRadius: 8,          
        }}>
          <div style={{ fontSize: 38, marginBottom: 16 }}><BarChart3 style={{color: 'blue'}} /></div>
          <h4 style={{ margin: '0 0 8px 0' }}>No Performance Data Found</h4>
          <p style={{ margin: 0, marginBottom: 16 }}>
            No KPIs, KRAs, or tasks found for this Organisation.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card style={{ padding: 16, marginBottom: 24}}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h4 style={{ margin: 16 }}>KPIs & KRAs Performance</h4>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ display: 'flex', gap: 4, backgroundColor: '#f0f0f0', borderRadius: 6, padding: 4 }}>
            <button
              onClick={() => setActiveChart('both')}
              style={{
                padding: '6px 12px',
                backgroundColor: activeChart === 'both' ? '#007bff' : 'transparent',
                color: activeChart === 'both' ? 'white' : '#666',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12
              }}
            >
              Both
            </button>
            <button
              onClick={() => setActiveChart('kpi')}
              style={{
                padding: '6px 12px',
                backgroundColor: activeChart === 'kpi' ? '#007bff' : 'transparent',
                color: activeChart === 'kpi' ? 'white' : '#666',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12
              }}
            >
              KPI Only
            </button>
            <button
              onClick={() => setActiveChart('kra')}
              style={{
                padding: '6px 12px',
                backgroundColor: activeChart === 'kra' ? '#007bff' : 'transparent',
                color: activeChart === 'kra' ? 'white' : '#666',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12
              }}
            >
              KRA Only
            </button>
          </div>
          <button 
            onClick={handleRefresh}
            style={{
              padding: '6px 12px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 12,
              marginRight: 16
            }}
          >
            Refresh
          </button>
        </div>
      </div>
      
      {/* Summary Statistics Cards */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 12,
        marginBottom: 24,
        margin: 18
      }}>
        <div style={{ 
          padding: 16,
          backgroundColor: '#e7f3ff',
          borderRadius: 8,
          textAlign: 'center',
          border: '1px solid #b3d9ff'
        }}>
          <div style={{ fontWeight: '600', color: '#0088FE', fontSize: 12 }}>TOTAL KPIs</div>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#0056b3' }}>{statistics.totalKPIs}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{statistics.completedKPIs} completed</div>
        </div>
        
        <div style={{ 
          padding: 16,
          backgroundColor: '#e6f7f0',
          borderRadius: 8,
          textAlign: 'center',
          border: '1px solid #a3e9cb'
        }}>
          <div style={{ fontWeight: '600', color: '#00C49F', fontSize: 12 }}>AVG KPI PROGRESS</div>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#00875a' }}>{statistics.avgKPIProgress}%</div>
          <div style={{ fontSize: 12, color: '#666' }}>{statistics.kpiCompletionRate}% completion rate</div>
        </div>
        
        <div style={{ 
          padding: 16,
          backgroundColor: '#fff4e6',
          borderRadius: 8,
          textAlign: 'center',
          border: '1px solid #ffd8a8'
        }}>
          <div style={{ fontWeight: '600', color: '#FFBB28', fontSize: 12 }}>TOTAL KRAs</div>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#e67700' }}>{statistics.totalKRAs}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{statistics.completedKRAs} completed</div>
        </div>
        
        <div style={{ 
          padding: 16,
          backgroundColor: '#ffe6e6',
          borderRadius: 8,
          textAlign: 'center',
          border: '1px solid #ffb3b3'
        }}>
          <div style={{ fontWeight: '600', color: '#FF8042', fontSize: 12 }}>TASK COMPLETION</div>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#cc0000' }}>{statistics.taskCompletionRate}%</div>
          <div style={{ fontSize: 12, color: '#666' }}>{statistics.completedTasks}/{statistics.totalTasks} tasks</div>
        </div>
      </div>

      {/* Summary Charts Section */}
      {(activeChart === 'both' || activeChart === 'kpi') && extractKPIs.length > 0 && (
        <div style={{ 
          marginBottom: 24,
          padding: 16,
          backgroundColor: '#f8f9fa',
          borderRadius: 8,
          border: '1px solid #dee2e6',
          margin: 18
        }}>
          <h4 style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <PieChartIcon size={18} />
            KPI Performance Summary
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* KPI Donut Chart */}
            <div>
              <div style={{ height: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={kpiDistributionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {kpiDistributionData.map((entry, index) => (
                        <PieCell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      formatter={(value) => <span style={{ fontSize: 11 }}>{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* KPI Gauge Summary */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'center',
              alignItems: 'center',
              gap: 16
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>Overall Average Progress</div>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <div style={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    background: `conic-gradient(
                      ${aggregatedData.kpi.average >= 80 ? '#00C49F' : aggregatedData.kpi.average >= 60 ? '#FFBB28' : '#FF8042'} 0deg ${aggregatedData.kpi.average * 3.6}deg,
                      #e9ecef ${aggregatedData.kpi.average * 3.6}deg 360deg
                    )`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{
                      width: 90,
                      height: 90,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column'
                    }}>
                      <div style={{ fontSize: 28, fontWeight: 'bold', color: aggregatedData.kpi.average >= 80 ? '#00C49F' : aggregatedData.kpi.average >= 60 ? '#FFBB28' : '#FF8042' }}>
                        {aggregatedData.kpi.average}%
                      </div>
                      <div style={{ fontSize: 10, color: '#666' }}>Average</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 'bold', color: '#00C49F' }}>
                    {aggregatedData.kpi.completionRate}%
                  </div>
                  <div style={{ fontSize: 11, color: '#666' }}>Completion Rate</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 'bold', color: '#0088FE' }}>
                    {aggregatedData.kpi.completed}/{aggregatedData.kpi.total}
                  </div>
                  <div style={{ fontSize: 11, color: '#666' }}>Completed KPIs</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {(activeChart === 'both' || activeChart === 'kra') && extractKRAs.length > 0 && (
        <div style={{ 
          marginBottom: 24,
          padding: 16,
          backgroundColor: '#f8f9fa',
          borderRadius: 8,
          border: '1px solid #dee2e6',
          margin: 18
        }}>
          <h4 style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <PieChartIcon size={18} />
            KRA Performance Summary
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* KRA Donut Chart */}
            <div>
              <div style={{ height: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={kraDistributionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {kraDistributionData.map((entry, index) => (
                        <PieCell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      formatter={(value) => <span style={{ fontSize: 11 }}>{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* KRA Gauge Summary */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'center',
              alignItems: 'center',
              gap: 16
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>Overall Average Progress</div>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <div style={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    background: `conic-gradient(
                      ${aggregatedData.kra.average >= 80 ? '#00C49F' : aggregatedData.kra.average >= 60 ? '#FFBB28' : '#FF8042'} 0deg ${aggregatedData.kra.average * 3.6}deg,
                      #e9ecef ${aggregatedData.kra.average * 3.6}deg 360deg
                    )`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{
                      width: 90,
                      height: 90,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column'
                    }}>
                      <div style={{ fontSize: 28, fontWeight: 'bold', color: aggregatedData.kra.average >= 80 ? '#00C49F' : aggregatedData.kra.average >= 60 ? '#FFBB28' : '#FF8042' }}>
                        {aggregatedData.kra.average}%
                      </div>
                      <div style={{ fontSize: 10, color: '#666' }}>Average</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 'bold', color: '#00C49F' }}>
                    {aggregatedData.kra.completionRate}%
                  </div>
                  <div style={{ fontSize: 11, color: '#666' }}>Completion Rate</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 'bold', color: '#FFBB28' }}>
                    {aggregatedData.kra.completed}/{aggregatedData.kra.total}
                  </div>
                  <div style={{ fontSize: 11, color: '#666' }}>Completed KRAs</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 24 }}>
        {/* KPI Section - Adaptive Visualization */}
        <div>
          <h4 style={{ margin: 16 }}>
            Key Performance Indicators ({statistics.totalKPIs})
            {extractKPIs.length > 15 && <span style={{ fontSize: 12, fontWeight: 'normal', marginLeft: 8, color: '#6c757d' }}></span>}
          </h4>
          
          {extractKPIs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6c757d', backgroundColor: '#f8f9fa', borderRadius: 8 }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}><BarChart3 /></div>
              <div style={{ fontWeight: '600', marginBottom: 4 }}>No KPIs Found</div>
              <div>Create KPIs to track performance metrics</div>
            </div>
          ) : useTableView ? (
            // Table View for 9+ KPIs
            <div>
              {/* Search and Filter Bar */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: 16,
                gap: 12,
                flexWrap: 'wrap',
                margin: 10
              }}>

                <SearchIcon size={16} />

                <div style={{ flex: 1, minWidth: 200 }}>
                  
                  <input
                    type="text"
                    placeholder="Search KPIs or objectives..."
                    value={kpiSearchTerm}
                    onChange={(e) => setKpiSearchTerm(e.target.value)}
                    style={{
                      width: '50%',
                      padding: '8px 12px',
                      border: '1px solid #dee2e6',
                      borderRadius: 4,
                      fontSize: 13,
                      
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    value={kpiSortBy}
                    onChange={(e) => setKpiSortBy(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #dee2e6',
                      borderRadius: 4,
                      fontSize: 13,
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="progress">Sort by Progress</option>
                    <option value="name">Sort by Name</option>
                    <option value="tasks">Sort by Task Completion</option>
                  </select>
                  <button
                    onClick={() => setKpiSortOrder(kpiSortOrder === 'asc' ? 'desc' : 'asc')}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #dee2e6',
                      borderRadius: 4,
                      fontSize: 13,
                      color: '#ffffff',
                      cursor: 'pointer',
                      backgroundColor: '#2c4af5',
                      boxShadow: '0 4px 8px rgb(108, 117, 242)'
                    }}
                  >
                    {kpiSortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
                  </button>
                </div>
              </div>

              {/* Summary Stats */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 16px',
                backgroundColor: '#dce9f5',
                borderRadius: 6,
                fontSize: 12,
                color: '#6c757d',
                margin: 16
                
              }}>
                <span style= {{height: 14, justifyContent: 'center', gap: 4, display: 'flex'}}>
                  <ViewIcon size={16} /> Showing {filteredKPIs.length} of {extractKPIs.length} KPIs
                </span>
                <span style= {{height: 14, justifyContent: 'center', gap: 4, display: 'flex'}}> 
                  <GiProgression size={16} /> Average Progress: {statistics.avgKPIProgress}%
                </span>
                <span style= {{height: 14, justifyContent: 'center', gap: 4, display: 'flex'}}>
                  <CheckCircle size={16} /> Completed: {statistics.completedKPIs}
                </span>
              </div>

              {/* Table View */}
              <div style={{ 
                maxHeight: 500, 
                overflowY: 'auto',
                border: '1px solid #dee2e6',
                borderRadius: 6,
                margin: 18
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                    <tr>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>KPI Name</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Objective</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Progress</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Tasks</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredKPIs.map((kpi, idx) => (
                      <tr key={kpi.id} style={{ borderBottom: idx === filteredKPIs.length - 1 ? 'none' : '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', fontWeight: '500' }}>{kpi.name}</td>
                        <td style={{ padding: '12px', fontSize: 12, color: '#6c757d' }}>{kpi.parentObjectiveName}</td>
                        <td style={{ padding: '12px', width: 200 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{
                                width: '100%',
                                height: 6,
                                backgroundColor: '#e9ecef',
                                borderRadius: 3,
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${kpi.progress}%`,
                                  height: '100%',
                                  backgroundColor: kpi.progress >= 80 ? '#00C49F' : kpi.progress >= 60 ? '#FFBB28' : '#FF8042',
                                  borderRadius: 3
                                }} />
                              </div>
                            </div>
                            <span style={{ fontSize: 12, fontWeight: '600', minWidth: 40 }}>
                              {kpi.progress}%
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center', fontSize: 12 }}>
                          {kpi.completedTasks}/{kpi.linkedTasks}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: '600',
                            backgroundColor: kpi.progress >= 80 ? '#d4edda' : kpi.progress >= 60 ? '#fff3cd' : '#f8d7da',
                            color: kpi.progress >= 80 ? '#155724' : kpi.progress >= 60 ? '#856404' : '#721c24'
                          }}>
                            {kpi.progress >= 80 ? 'Excellent' : kpi.progress >= 60 ? 'Good' : 'Needs Work'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {filteredKPIs.length === 0 && (
                  <div style={{ textAlign: 'center', padding: 40, color: '#6c757d' }}>
                    No KPIs match your search
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Card View for 8 or fewer KPIs
            <div style={{ maxHeight: 400, overflowY: 'auto', margin: 8 }}>
              {extractKPIs.map((kpi, index) => (
                <div key={kpi.id} style={{ 
                  padding: 12, 
                  marginBottom: 8, 
                  border: '1px solid #e9ecef',
                  borderRadius: 6,
                  backgroundColor: '#ffffff',
                   }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontWeight: '600', fontSize: 13 }}>
                      {kpi.name}
                    </div>
                    <div style={{ 
                      padding: '2px 8px',
                      backgroundColor: kpi.progress >= 80 ? '#00C49F' : kpi.progress >= 60 ? '#FFBB28' : '#FF8042',
                      color: 'white',
                      borderRadius: 12,
                      fontSize: 11,
                      fontWeight: 'bold'
                    }}>
                      {kpi.progress}%
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: 6 }}>
                    <div style={{ 
                      width: '100%',
                      height: 6,
                      backgroundColor: '#e9ecef',
                      borderRadius: 3,
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${kpi.progress}%`,
                        height: '100%',
                        backgroundColor: kpi.progress >= 80 ? '#00C49F' : kpi.progress >= 60 ? '#FFBB28' : '#FF8042',
                        borderRadius: 3,
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                  
                  <div style={{ fontSize: 11, color: '#6c757d', display: 'flex', justifyContent: 'space-between' }}>
                    <span>🎯 {kpi.parentObjectiveName}</span>
                    <span>✅ {kpi.completedTasks}/{kpi.linkedTasks} tasks</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* KRA Section */}
        <div>
          <h4 style={{ marginBottom: 16, margin: 18 }}>Key Result Areas ({statistics.totalKRAs})</h4>
          {extractKRAs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6c757d', backgroundColor: '#f8f9fa', borderRadius: 8 }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}>🎯</div>
              <div style={{ fontWeight: '600', marginBottom: 4 }}>No KRAs Found</div>
              <div>Create KRAs to track key result areas</div>
            </div>
          ) : (
            <div style={{ maxHeight: 400, overflowY: 'auto', margin: 18 }}>
              {extractKRAs.map((kra, index) => (
                <div key={kra.id} style={{ 
                  padding: 16, 
                  marginBottom: 12, 
                  border: '1px solid #e9ecef',
                  borderRadius: 8,
                  borderLeft: `4px solid ${['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'][index % 5]}`,
                  backgroundColor: '#f8f9fa'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    marginBottom: 8 
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: 14, marginBottom: 4 }}>
                        {kra.name}
                      </div>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                        Parent: {kra.parentObjectiveName}
                      </div>
                     
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          fontSize: 12, 
                          marginBottom: 4 
                        }}>
                          <span>Overall Progress</span>
                          <span>{kra.progress}%</span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: 6,
                          backgroundColor: '#e9ecef',
                          borderRadius: 3,
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${kra.progress}%`,
                            height: '100%',
                            backgroundColor: ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'][index % 5],
                            borderRadius: 3,
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>
                    </div>
                    <div style={{ 
                      padding: '4px 8px',
                      backgroundColor: ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'][index % 5],
                      color: 'white',
                      borderRadius: 12,
                      fontSize: 12,
                      fontWeight: 'bold',
                      minWidth: 50,
                      textAlign: 'center'
                    }}>
                      {kra.weight}%
                    </div>
                  </div>
                  
                  {kra.description && (
                    <div style={{ 
                      fontSize: 12, 
                      color: '#6c757d', 
                      lineHeight: 1.4,
                      marginBottom: 8
                    }}>
                      {kra.description}
                    </div>
                  )}
                  
                  <div style={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: 11,
                    color: '#6c757d'
                  }}>
                    <div>
                      {kra.linkedKPIs} linked KPIs • Created: {new Date(kra.date).toLocaleDateString()}
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center' 
                    }}>
                      <div 
                        style={{
                          width: 8,
                          height: 8,
                          backgroundColor: ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'][index % 5],
                          borderRadius: '50%',
                          marginRight: 4
                        }}
                      />
                      Weight
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}

export default KPIKRAReports